import { FC } from 'react'
import { useRouter } from 'next/router';
import { Box } from 'components/primitives'
import { useTokens } from '@reservoir0x/reservoir-kit-ui';

const BLUR = 'https://blur.io'
const MAGIC_EDEN = 'https://magiceden.io'
const OPEN_SEA = 'https://opensea.io'

const getBlurUrl = (address: string, tokenId: string) =>
  (!address || !tokenId) ? BLUR : `${BLUR}/asset/${address}/${tokenId}`

const getMagicEdenUrl = (address: string, tokenId: string, chain = 'ethereum') => {
  if (!address || !tokenId) return MAGIC_EDEN

  switch (chain) {
    case 'ordinals':
      return `${MAGIC_EDEN}/ordinals/item-details/${tokenId}`
    default:
      return `${MAGIC_EDEN}/item-details/${chain}/${address}/${tokenId || ''}`
  }
}

const getOpenSeaUrl = (address: string, tokenId: string, chain = 'ethereum') => {
  if (!address || !tokenId) return OPEN_SEA

  switch (chain) {
    case 'polygon':
      return `${OPEN_SEA}/assets/matic/${address}/${tokenId}`
    default:
      return `${OPEN_SEA}/assets/${chain}/${address}/${tokenId}`
  }
}

type Props = {
  token: ReturnType<typeof useTokens>['data'][0] | null
}

const getSource = (name: string, href: string) => {
  switch (name) {
    case 'NFTX':
      return {
        href: `https://${href}`,
        icon: '/icons/nftx_default.png'
      };
    case 'sudoswap':
      return {
        href,
        icon: '/icons/sudoswap_default.png'
      }
    default:
      return null
  }
}

const Icon = ({ href, icon }: { href: string, icon: string }) => (
  <Box css={{ padding: '0.4em', ':hover': { opacity: 0.75, transition: 'all 100ms ease 0s' } }}>
    <a href={href} target='_blank'>
      <img src={icon} alt='social' width={32}/>
    </a>
  </Box>
)

const BlurIcon: FC<Props & { chain: string }> = ({ token, chain }) => {
  const excludeChain = ['polygon']

  if (excludeChain.includes(chain))
    return null

  return (
    <Icon
      href={getBlurUrl(token?.token?.contract as string, token?.token?.tokenId as string)}
      icon={'/icons/blur_default.png'}
    />
  )
}

const MagicEdenIcon: FC<Props & { chain: string }> =  ({ token, chain }) => {
  const excludeCollection = [
    '0x5af7678c2af78497c48340e42e34af4546440e0d' // Runes Trading Cards
  ]

  if (excludeCollection.includes(token?.token?.collection?.id as string))
    return null

  return (
    <Icon
      href={getMagicEdenUrl(token?.token?.contract as string, token?.token?.tokenId as string, chain)}
      icon={'/icons/magic_eden_default.png'}
    />
  )
}

const GmIcon: FC<Props> = ({ token }) => {
  const includeCollection = [
    '0x521f9c7505005cfa19a8e5786a9c3c9c9f5e6f42' // Wizards
  ]

  if (!includeCollection.includes(token?.token?.collection?.id as string))
    return null

  return (
    <Icon
      href={`https://forgottenrunes.com/scenes/gm/${token?.token?.tokenId}`}
      icon={'/icons/gm.png'}
    />
  )
}

export const SocialLinks: FC<Props> = ({ token }) => {
  const socials = []
  const router = useRouter()

  const askSource = token?.market?.floorAsk?.source
    ? getSource(
      token?.market?.floorAsk?.source?.name as string,
      token?.market?.floorAsk?.source?.url
      || token?.market?.floorAsk?.source?.domain as string
    )
    : null

  if (askSource) socials.push(askSource)

  const bidSource = (
    token?.market?.topBid?.source
      && token?.market?.topBid?.source?.name !== token?.market?.floorAsk?.source?.name
  )
    ? getSource(
      token?.market?.topBid?.source?.name as string,
      token?.market?.topBid?.source?.url
      || token?.market?.topBid?.source?.domain as string
    )
    : null

  if (bidSource) socials.push(bidSource)

  return (
    <Box css={{ display: 'flex', justifyContent: 'end', margin: '10px 0', alignItems: 'center' }}>
      <BlurIcon token={token} chain={router.query?.chain as string} />
      <MagicEdenIcon token={token} chain={router.query?.chain as string}  />
      <Icon
        href={getOpenSeaUrl(token?.token?.contract as string, token?.token?.tokenId as string, router.query?.chain as string)}
        icon={'/icons/opensea_default.png'}
      />
      {
        socials.length
        ? (socials.map(({ href, icon }) => <Icon href={href} icon={icon} />))
        : null
      }
      <GmIcon token={token} />
      {
        token?.token?.collection?.symbol
        ? (
          <>
            <Icon
              href={`https://forgottenrunes.com/lockscreen?tokenSlug=${token?.token?.collection?.symbol?.toLowerCase()}&tokenId=${token?.token?.tokenId}`}
              icon={'/icons/social_phone_default.png'}
            />
            <Icon
              href={`https://forgottenrunes.com/api/art/${token?.token?.collection?.symbol?.toLowerCase()}/${token?.token?.tokenId}.zip`}
              icon={'/icons/social_download_default_w.png'}
            />
            <Icon
              href={`https://forgottenrunes.com/lore/${token?.token?.collection?.symbol?.toLowerCase()}/${token?.token?.tokenId}/0`}
              icon={'/icons/social_link_default.png'}
            />
          </>
        )
        : null
      }
    </Box>
  )
}